import {ExtractionRunStatus} from '@sollapay/enums';
import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useMemo, useRef, useState} from 'react';

import {documentProcessingKeys} from '@/features/documents';
import {useApiClient} from '@/hooks';
import {logger} from '@/utils';

import {extractionApi} from '../api';
import {
  buildAiRunEvaluations,
  filterLiveAiFields,
  isEmptyAiValue,
  submitAiRunEvaluations
} from '../utils';

import type {AiSuggestion} from '../types';
import type {DocumentProcessingStatusDto, ExtractionRunDto} from '@sollapay/types';
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormGetValues,
  UseFormSetValue
} from 'react-hook-form';

interface UseAiFormSessionParams<TFieldValues extends FieldValues = FieldValues> {
  documentStatuses: Array<DocumentProcessingStatusDto | undefined>;
  getValues: UseFormGetValues<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  /** Maps API targetField to form field key. Defaults to returning targetField as-is. Return null to skip. */
  resolveField?: (targetField: string, scopeId: string) => string | null;
  /** Converts extracted string to the form field's value type. Defaults to identity. */
  toFieldValue?: (field: string, extractedValue: string) => string;
  /** Overrides the empty-value predicate. Defaults to null/undefined/blank string. */
  isEmptyValue?: (value: unknown) => boolean;
  /** Called after each batch of completed runs is processed. */
  onSuggestionsApplied?: (autoFilledCount: number) => void;
  scopeIdByDocumentId?: ReadonlyMap<string, string>;
  liveDocumentIds: ReadonlySet<string>;
}

const log = logger.createLogger('useAiFormSession');

const DEFAULT_RESOLVE_FIELD = (targetField: string): string => targetField;

const DEFAULT_TO_FIELD_VALUE = (_field: string, v: string): string => v;

// Persists processed run IDs across remounts so cancel → reopen skips already-handled runs.
const PROCESSED_RUNS_QC_KEY = ['ai-form-session', 'processed-run-ids'] as const;

/**
 * Manages AI extraction state for react-hook-form forms.
 * Processes completed extraction runs, auto-fills empty fields, tracks modifications,
 * and exposes submitEvaluations / clearAiState for save and cancel flows.
 */
export function useAiFormSession<TFieldValues extends FieldValues = FieldValues>(
  params: UseAiFormSessionParams<TFieldValues>
) {
  const resolveField = params.resolveField ?? DEFAULT_RESOLVE_FIELD;
  const toFieldValue = params.toFieldValue ?? DEFAULT_TO_FIELD_VALUE;
  const isEmpty = params.isEmptyValue ?? isEmptyAiValue;

  const {documentStatuses, onSuggestionsApplied, liveDocumentIds} = params;

  // Refs keep latest form methods without triggering effect re-runs.
  const getValuesRef = useRef(params.getValues);
  getValuesRef.current = params.getValues;
  const setValueRef = useRef(params.setValue);
  setValueRef.current = params.setValue;
  const scopeIdByDocumentIdRef = useRef(params.scopeIdByDocumentId);
  scopeIdByDocumentIdRef.current = params.scopeIdByDocumentId;

  const [aiFields, setAiFields] = useState(() => new Map<string, AiSuggestion>());
  const [autoFilledFields, setAutoFilledFields] = useState(() => new Set<string>());
  const [modifiedFields, setModifiedFields] = useState(() => new Set<string>());

  // Derived, not pruned: a removed document's runs are purged, so its suggestions are hidden and never evaluated.
  const liveAiFields = useMemo(
    () => filterLiveAiFields({suggestionsByField: aiFields, liveDocumentIds}),
    [aiFields, liveDocumentIds]
  );

  const client = useApiClient();
  const queryClient = useQueryClient();

  // Seed from QueryClient on mount so cancel → remount doesn't re-process prior runs.
  const processedRunIdsRef = useRef(
    new Set<string>(queryClient.getQueryData<string[]>(PROCESSED_RUNS_QC_KEY) ?? [])
  );

  useEffect(() => {
    const newRuns: Array<{run: ExtractionRunDto; documentId: string}> = [];

    for (const status of documentStatuses) {
      if (!status?.context || !('extractionRun' in status.context)) continue;
      const run = status.context.extractionRun;

      if (
        run.status === ExtractionRunStatus.COMPLETED &&
        run.evaluation === null &&
        !processedRunIdsRef.current.has(run.id)
      ) {
        newRuns.push({run, documentId: run.documentId});
      }
    }

    if (newRuns.length === 0) return;

    const suggestions = new Map<string, AiSuggestion>();
    const formUpdates: Array<{field: string; value: string}> = [];
    const newlyFilledFields = new Set<string>();

    for (const {run, documentId} of newRuns) {
      // Guessing an unknown scope would land one row's extracted values on another row's fields.
      const scopeMap = scopeIdByDocumentIdRef.current;
      const scopeId = scopeMap ? scopeMap.get(documentId) : '';
      if (scopeId === undefined) continue;

      for (const s of run.suggestions) {
        const fieldKey = resolveField(s.targetField, scopeId);
        if (fieldKey === null || suggestions.has(fieldKey)) continue; // first run wins per field

        const currentValue = getValuesRef.current(fieldKey as Path<TFieldValues>);
        const convertedValue = toFieldValue(fieldKey, s.extractedValue);

        const suggestion: AiSuggestion = {
          value: s.extractedValue,
          confidence: s.confidence,
          targetField: s.targetField,
          runId: run.id,
          documentId,
          scopeId
        };

        if (isEmpty(currentValue)) {
          formUpdates.push({field: fieldKey, value: convertedValue});
          newlyFilledFields.add(fieldKey);
          suggestions.set(fieldKey, suggestion);
        } else if (String(currentValue ?? '') === String(convertedValue)) {
          // Field already matches suggestion (e.g. remounted after save) — badge only.
          suggestions.set(fieldKey, suggestion);
        }
        // Pre-existing different value — no badge, no override.
      }
    }

    newRuns.forEach(({run}) => processedRunIdsRef.current.add(run.id));

    if (suggestions.size === 0) return;

    for (const {field, value} of formUpdates) {
      setValueRef.current(
        field as Path<TFieldValues>,
        value as PathValue<TFieldValues, Path<TFieldValues>>,
        {shouldDirty: true, shouldValidate: true}
      );
    }

    setAiFields(prev => new Map([...prev, ...suggestions]));
    // A field's flags belong to its current suggestion, so a replaced suggestion's flags go with it.
    setAutoFilledFields(
      prev => new Set([...[...prev].filter(field => !suggestions.has(field)), ...newlyFilledFields])
    );
    setModifiedFields(prev => new Set([...prev].filter(field => !suggestions.has(field))));

    onSuggestionsApplied?.(newlyFilledFields.size);
  }, [documentStatuses, onSuggestionsApplied, resolveField, toFieldValue, isEmpty]);

  /**
   * POSTs evaluation data to /extraction/evaluate then resets AI state. Call after save.
   * Best-effort: a failed evaluation is logged, never thrown, and never blocks the reset.
   */
  const submitEvaluations = async (): Promise<void> => {
    if (aiFields.size === 0) return;

    const payloadByRunId = buildAiRunEvaluations({
      suggestionsByField: liveAiFields,
      resolveSavedValue: field => String(getValuesRef.current(field as Path<TFieldValues>) ?? ''),
      isAutoFilledField: field => autoFilledFields.has(field),
      isModifiedField: field => modifiedFields.has(field)
    });

    const {documentIds, failures} = await submitAiRunEvaluations({
      payloadByRunId,
      documentIdByRunId: new Map([...liveAiFields.values()].map(s => [s.runId, s.documentId])),
      submit: (documentId, payload) =>
        extractionApi.submitExtractionEvaluation(client, documentId, payload)
    });
    failures.forEach(reason => log.warn('Failed to submit extraction evaluation', reason));

    // Invalidate so remount sees evaluation !== null and skips re-filling.
    for (const docId of documentIds) {
      void queryClient.invalidateQueries({
        queryKey: documentProcessingKeys.byDocument(docId)
      });
    }

    setAiFields(new Map());
    setAutoFilledFields(new Set());
    setModifiedFields(new Set());
  };

  /** Clears AI badges without posting evaluations — one scope when `scopeId` is given, else all. */
  const clearAiState = (scopeId?: string): void => {
    // Persist processed IDs so cancel → reopen skips them.
    queryClient.setQueryData<string[]>(PROCESSED_RUNS_QC_KEY, [...processedRunIdsRef.current]);

    if (scopeId === undefined) {
      setAiFields(new Map());
      setAutoFilledFields(new Set());
      setModifiedFields(new Set());
      return;
    }

    const cleared = new Set(
      [...aiFields].filter(([, s]) => s.scopeId === scopeId).map(([field]) => field)
    );
    if (cleared.size === 0) return;

    setAiFields(prev => new Map([...prev].filter(([field]) => !cleared.has(field))));
    setAutoFilledFields(prev => new Set([...prev].filter(field => !cleared.has(field))));
    setModifiedFields(prev => new Set([...prev].filter(field => !cleared.has(field))));
  };

  /**
   * Removes AI state entries for a removed field-array item and re-keys higher-index entries.
   * Call this alongside react-hook-form's `remove(index)` for any field array so the auto-grow
   * effect (which watches aiFields) does not immediately re-append the removed row.
   *
   * Example: fieldArrayPrefix = 'buyers', removedIndex = 1
   *   'buyers.1.fullName' → removed
   *   'buyers.2.email'    → 'buyers.1.email'
   *   'buyers.0.name'     → unchanged
   */
  const reindexFieldArray = ({
    fieldArrayPrefix,
    removedIndex
  }: {
    fieldArrayPrefix: string;
    removedIndex: number;
  }): void => {
    const reindex = (key: string): string | null => {
      if (!key.startsWith(`${fieldArrayPrefix}.`)) return key;
      const after = key.slice(fieldArrayPrefix.length + 1); // "N.field..."
      const dotPos = after.indexOf('.');
      if (dotPos === -1) return key;
      const idx = parseInt(after.slice(0, dotPos), 10);
      if (isNaN(idx)) return key;
      const field = after.slice(dotPos); // ".fieldName"
      if (idx === removedIndex) return null;
      if (idx > removedIndex) return `${fieldArrayPrefix}.${idx - 1}${field}`;
      return key;
    };

    setAiFields(prev => {
      const next = new Map<string, AiSuggestion>();
      for (const [k, v] of prev) {
        const nk = reindex(k);
        if (nk !== null) next.set(nk, v);
      }
      return next;
    });
    setAutoFilledFields(prev => {
      const next = new Set<string>();
      for (const k of prev) {
        const nk = reindex(k);
        if (nk !== null) next.add(nk);
      }
      return next;
    });
    setModifiedFields(prev => {
      const next = new Set<string>();
      for (const k of prev) {
        const nk = reindex(k);
        if (nk !== null) next.add(nk);
      }
      return next;
    });
  };

  const {suggestionsCount, suggestionsCountByScope} = useMemo(() => {
    const byScope = new Map<string, number>();
    let total = 0;
    for (const {scopeId, dismissed} of liveAiFields.values()) {
      if (dismissed) continue;
      byScope.set(scopeId, (byScope.get(scopeId) ?? 0) + 1);
      total += 1;
    }
    return {suggestionsCount: total, suggestionsCountByScope: byScope};
  }, [liveAiFields]);

  return {
    aiFields: liveAiFields,
    suggestionsCount,
    suggestionsCountByScope,
    markFieldModified: (field: string) => {
      setModifiedFields(prev => new Set([...prev, field]));
      setAiFields(prev => {
        const suggestion = prev.get(field);
        if (!suggestion || suggestion.dismissed) return prev;
        const next = new Map(prev);
        next.set(field, {...suggestion, dismissed: true});
        return next;
      });
    },
    submitEvaluations,
    clearAiState,
    reindexFieldArray
  };
}
